import { useCallback, useEffect, useRef, useState } from 'react';

import TagMarker from '@modules/product-tagging/components/tag-with-modals/TagMarker';

import type { B2BAssets, FeatureSection } from '../types';
import { useLeadModal } from '../lead-form/useLeadModal';

export interface B2BFeatureProps {
    section: FeatureSection;
    assets: B2BAssets;
}

/**
 * Scroll-reveal state for the product tag. The trigger is keyed to the DOT
 * element (not the section): the section is tall and the dot sits near its
 * bottom, so observing the section would fire the sequence while the dot is
 * still below the fold. The `-25%` bottom rootMargin fires when the dot rises
 * into the lower-middle of the viewport, so the user actually sees it pulse,
 * then the card unfolds from it a beat later. `registerTarget` is attached to
 * both the desktop and mobile dot wrappers; the display-hidden one never
 * intersects, so only the active layout fires. Honours
 * `prefers-reduced-motion` by revealing everything at once with no animation.
 */
const useTagReveal = () => {
    const [dotVisible, setDotVisible] = useState(false);
    const [cardVisible, setCardVisible] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const cardTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const registerTarget = useCallback((node: HTMLElement | null) => {
        if (!node || typeof window === 'undefined') return;

        const prefersReduced = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (prefersReduced) {
            setDotVisible(true);
            setCardVisible(true);
            return;
        }

        if (!observerRef.current) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;
                    observerRef.current?.disconnect();
                    setDotVisible(true);
                    // Let the dot pulse first, then unfold the card.
                    cardTimerRef.current = setTimeout(
                        () => setCardVisible(true),
                        900,
                    );
                },
                { rootMargin: '0px 0px -25% 0px', threshold: 0 },
            );
        }
        observerRef.current.observe(node);
    }, []);

    useEffect(
        () => () => {
            observerRef.current?.disconnect();
            clearTimeout(cardTimerRef.current);
        },
        [],
    );

    return { registerTarget, dotVisible, cardVisible };
};

/**
 * Discover Tagging (Figma `478:24503`, 1920 × 1318) — and any other
 * eyebrow+title+media+body+CTA feature band.
 *
 * Built on the 1920-frame coordinate system: `aspect-[1920/frameHeight]` with
 * absolute-positioned children expressed in % / vw so the layout scales
 * proportionally at any viewport. Mobile (≤md) relaxes to a flowed stack.
 *
 * Slot map (Figma coords inside the 1920×1318 frame):
 * | slot           | x       | y       | w       | h       |
 * | -------------- | ------- | ------- | ------- | ------- |
 * | arrow vector   |   25    |   10.30 |   57.84 |   61.86 |
 * | eyebrow        |  497.59 |   12.43 |   221   |   30    |
 * | title          |  497.59 |  -14.33 | 1361.49 |  344    |
 * | media frame    |   31    |  546    |  975    | 1252    |
 * | product card   |  380    | 1057    |  339    |   95    |
 * | body           | 1119    |  538.67 |  636.05 |  347    |
 * | CTA pill       | 1119    |  905    |  230.27 |   60    |
 */
export const B2BFeature = ({ section, assets }: B2BFeatureProps) => {
    const {
        eyebrow,
        title,
        titleTail,
        body,
        ctaLabel,
        ctaHref = '#apply',
        ctaOpensModal,
        mediaAssetKey,
        isVideo,
        productCard,
        frameHeight = 1318,
    } = section;

    const { openModal } = useLeadModal();

    const mediaSrc = mediaAssetKey ? assets[mediaAssetKey] : undefined;
    const productImageSrc = productCard
        ? assets[productCard.productImageAssetKey]
        : undefined;

    const { registerTarget, dotVisible, cardVisible } = useTagReveal();

    // Convert Figma px → frame %
    const pctX = (x: number) => (x / 1920) * 100;
    const pctY = (y: number) => (y / frameHeight) * 100;

    // Tag dot anchor, relative to the media frame. Defaults to the product
    // card's top-left corner so the card visibly unfolds from the dot;
    // `dotLeftPct` / `dotTopPct` on the productCard override it.
    const dotLeftPct = productCard?.dotLeftPct ?? ((380 - 31) / 975) * 100;
    const dotTopPct =
        productCard?.dotTopPct ?? ((1057 - 546) / (frameHeight - 546)) * 100;

    // Card unfold: fade + slide + scale from the dot, a beat after it pulses.
    const cardRevealClass = `origin-top-left transition-all duration-700 ease-out motion-reduce:transition-none ${
        cardVisible
            ? 'opacity-100 translate-y-0 scale-100'
            : 'pointer-events-none opacity-0 translate-y-1 scale-95'
    }`;

    return (
        <section
            className="relative w-full bg-white max-md:bg-transparent"
            style={{ aspectRatio: `1920 / ${frameHeight}` }}
        >
            {/* Desktop layout — absolute-positioned to match the 1920 frame */}
            <div className="absolute inset-0 max-md:hidden">
                {/* Arrow vector — top-left */}
                <svg
                    className="absolute"
                    style={{
                        left: `${pctX(25)}%`,
                        top: `${pctY(10.3)}%`,
                        width: `${pctX(57.84)}%`,
                        height: `${(61.86 / frameHeight) * 100}%`,
                    }}
                    viewBox="0 0 58 62"
                    fill="none"
                    aria-hidden="true"
                >
                    <path
                        d="M2 31 L54 31 M44 21 L54 31 L44 41"
                        stroke="#181818"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>

                {/* Eyebrow — (Discover Tagging) */}
                <p
                    className="absolute whitespace-nowrap font-sans text-[1.458vw] leading-[1.07] tracking-[-0.048em] text-[#181818]"
                    style={{
                        left: `${pctX(497.59)}%`,
                        top: `${pctY(12.43)}%`,
                    }}
                >
                    {eyebrow}
                </p>

                {/* Title — 80px Roman two-tone, first-line indent 295px */}
                {title && (
                    <p
                        className="absolute font-sans text-[4.167vw] leading-[1.07] tracking-[-0.048em] text-[#181818] [word-break:break-word]"
                        style={{
                            left: `${pctX(497.59)}%`,
                            top: `${pctY(-14.33)}%`,
                            width: `${pctX(1361.49)}%`,
                            textIndent: `${295 / 19.2}vw`,
                        }}
                    >
                        <span>{title}</span>
                        {titleTail && (
                            <span style={{ color: '#c4c4c4' }}>
                                {titleTail}
                            </span>
                        )}
                    </p>
                )}

                {/* Media frame — left image, top=546 + height fills to section
                 * bottom (frameHeight - 546). Original spec said h=1252 but
                 * the source image is sized within the frame, not overflowing;
                 * setting h to fill keeps the image flush to the section bottom
                 * (no off-grid white sliver) and places the product card overlay
                 * at the correct y. */}
                {mediaSrc && (
                    <div
                        className="absolute overflow-hidden"
                        style={{
                            left: `${pctX(31)}%`,
                            top: `${pctY(546)}%`,
                            width: `${pctX(975)}%`,
                            height: `${((frameHeight - 546) / frameHeight) * 100}%`,
                        }}
                    >
                        {isVideo ? (
                            <video
                                className="absolute inset-0 size-full object-cover object-top"
                                src={mediaSrc}
                                autoPlay
                                muted
                                loop
                                playsInline
                            />
                        ) : (
                            <img
                                className="absolute inset-0 size-full object-cover object-top"
                                src={mediaSrc}
                                alt=""
                            />
                        )}

                        {/* Reused product-tag dot (from photo tagging). Pulses
                         * on scroll-in, then opens as the card reveals. */}
                        {productCard && productImageSrc && (
                            <div
                                ref={registerTarget}
                                aria-hidden="true"
                                className={`pointer-events-none absolute z-10 transition-opacity duration-300 ${
                                    dotVisible ? 'opacity-100' : 'opacity-0'
                                }`}
                                style={{
                                    left: `${dotLeftPct}%`,
                                    top: `${dotTopPct}%`,
                                }}
                            >
                                <TagMarker isApproved />
                            </div>
                        )}

                        {/* Product-card overlay — bottom-third of the image */}
                        {productCard && productImageSrc && (
                            <div
                                className={`absolute flex items-center gap-[0.573vw] rounded-[0.313vw] bg-[rgba(250,250,250,0.85)] px-[0.625vw] py-[0.417vw] shadow-[inset_0_0_4px_0_rgba(0,0,0,0.18)] backdrop-blur-[5px] ${cardRevealClass}`}
                                style={{
                                    // 380 px from frame-left → relative to media at x=31:
                                    // local x = (380 - 31) / 975 = 35.79 %
                                    left: `${((380 - 31) / 975) * 100}%`,
                                    // 1057 px from frame-top → relative to media at y=546:
                                    // local y = (1057 - 546) / mediaH
                                    top: `${((1057 - 546) / (frameHeight - 546)) * 100}%`,
                                    width: `${(339 / 975) * 100}%`,
                                    height: `${(95 / (frameHeight - 546)) * 100}%`,
                                }}
                            >
                                <img
                                    className="aspect-square h-[80%] rounded-[4px] object-cover"
                                    src={productImageSrc}
                                    alt=""
                                />
                                <div className="flex min-w-0 flex-1 flex-col justify-center">
                                    <p className="font-sans text-[0.833vw] font-medium leading-tight text-[#141414]">
                                        {productCard.productName}
                                    </p>
                                    <p className="truncate font-sans text-[0.833vw] leading-tight text-[#737373]">
                                        {productCard.brandName}
                                    </p>
                                    <p className="mt-[0.156vw] font-sans text-[0.521vw] font-medium uppercase leading-none tracking-[0.042em] text-[#141414]">
                                        {productCard.price}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Body — 25px Medium right column */}
                {body && (
                    <p
                        className="absolute whitespace-pre-line font-sans text-[1.302vw] font-medium leading-[1.1] tracking-[-0.030em] text-black [word-break:break-word]"
                        style={{
                            left: `${pctX(1119)}%`,
                            top: `${pctY(538.67)}%`,
                            width: `${pctX(636.054)}%`,
                        }}
                    >
                        {body}
                    </p>
                )}

                {/* CTA pill — Apply to Join. Opens the lead modal when
                 *  `ctaOpensModal` is set; otherwise navigates to `ctaHref`. */}
                {ctaLabel && (
                    <>
                        {(() => {
                            const ctaClassName =
                                'absolute inline-flex items-center justify-center rounded-full bg-[#333] font-sans font-bold text-white drop-shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]';
                            const ctaStyle = {
                                left: `${pctX(1119)}%`,
                                top: `${pctY(905)}%`,
                                minWidth: `${pctX(230.27)}%`,
                                height: `${(60 / frameHeight) * 100}%`,
                                paddingLeft: `${27.568 / 19.2}vw`,
                                paddingRight: `${27.568 / 19.2}vw`,
                                fontSize: `${22.703 / 19.2}vw`,
                                lineHeight: `${24.324 / 22.703}`,
                                letterSpacing: '-0.030em',
                            } as const;
                            return ctaOpensModal ? (
                                <button
                                    type="button"
                                    onClick={() => openModal({ ctaLabel })}
                                    className={`${ctaClassName} cursor-pointer`}
                                    style={ctaStyle}
                                >
                                    {ctaLabel}
                                </button>
                            ) : (
                                <a
                                    href={ctaHref}
                                    className={ctaClassName}
                                    style={ctaStyle}
                                >
                                    {ctaLabel}
                                </a>
                            );
                        })()}
                    </>
                )}
            </div>

            {/* Mobile layout — flowed stack using Phase-2 vw tokens (768
             * design frame): eyebrow 2.08 vw, title 5.21 vw, body 3.255 vw,
             * CTA 33.58 × 8.75 vw / 3.31 vw label. Gutter 5.4 %. No vertical
             * padding — `B2BLandingPage`'s `gap-[14.84vw]` controls inter-
             * section spacing so Phase-2 sections share the Phase-1 rhythm. */}
            <div className="hidden flex-col gap-[5vw] px-[5.4%] max-md:flex">
                <p className="font-sans text-[3.255vw] leading-[1.07] tracking-[-0.044em] text-[#181818]">
                    {eyebrow}
                </p>
                {title && (
                    <p className="font-sans text-[5.21vw] leading-[1.07] tracking-[-0.048em] text-[#181818] [word-break:break-word]">
                        <span>{title}</span>
                        {titleTail && (
                            <span className="text-[#c4c4c4]">{titleTail}</span>
                        )}
                    </p>
                )}
                {mediaSrc && (
                    <div className="relative aspect-975/772 w-full overflow-hidden bg-gray-200">
                        {isVideo ? (
                            <video
                                className="absolute inset-0 size-full object-cover"
                                src={mediaSrc}
                                autoPlay
                                muted
                                loop
                                playsInline
                            />
                        ) : (
                            <img
                                className="absolute inset-0 size-full object-cover"
                                src={mediaSrc}
                                alt=""
                            />
                        )}

                        {/* Reused product-tag dot (from photo tagging). */}
                        {productCard && productImageSrc && (
                            <div
                                ref={registerTarget}
                                aria-hidden="true"
                                className={`pointer-events-none absolute z-10 transition-opacity duration-300 ${
                                    dotVisible ? 'opacity-100' : 'opacity-0'
                                }`}
                                style={{
                                    left: `${dotLeftPct}%`,
                                    top: `${dotTopPct}%`,
                                }}
                            >
                                <TagMarker isApproved />
                            </div>
                        )}

                        {/* Product-card overlay — proportional to the mobile
                         * media frame (Figma 339×95 within 975 wide). */}
                        {productCard && productImageSrc && (
                            <div
                                className={`absolute flex items-center gap-[1vw] rounded-[0.8vw] bg-[rgba(250,250,250,0.85)] px-[1.5vw] py-[1vw] shadow-[inset_0_0_4px_0_rgba(0,0,0,0.18)] backdrop-blur-[5px] ${cardRevealClass}`}
                                style={{
                                    left: `${((380 - 31) / 975) * 100}%`,
                                    top: `${((1057 - 546) / (frameHeight - 546)) * 100}%`,
                                    width: `${(339 / 975) * 100}%`,
                                    height: `${(95 / (frameHeight - 546)) * 100}%`,
                                }}
                            >
                                <img
                                    className="aspect-square h-[80%] rounded-[2px] object-cover"
                                    src={productImageSrc}
                                    alt=""
                                />
                                <div className="flex min-w-0 flex-1 flex-col justify-center">
                                    <p className="truncate font-sans text-[1.7vw] font-medium leading-tight text-[#141414]">
                                        {productCard.productName}
                                    </p>
                                    <p className="truncate font-sans text-[1.7vw] leading-tight text-[#737373]">
                                        {productCard.brandName}
                                    </p>
                                    <p className="mt-[0.3vw] font-sans text-[1.1vw] font-medium uppercase leading-none tracking-[0.042em] text-[#141414]">
                                        {productCard.price}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {body && (
                    <p className="whitespace-pre-line font-sans text-[3.255vw] font-medium leading-[1.1] tracking-[-0.030em] text-[#181818]">
                        {body}
                    </p>
                )}
                {ctaLabel &&
                    (ctaOpensModal ? (
                        <button
                            type="button"
                            onClick={() => openModal({ ctaLabel })}
                            className="inline-flex h-[8.75vw] min-h-[2.75rem] w-[33.58vw] cursor-pointer items-center justify-center self-start rounded-full bg-[#333] font-sans text-[3.31vw] font-bold leading-[1.071] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]"
                        >
                            {ctaLabel}
                        </button>
                    ) : (
                        <a
                            href={ctaHref}
                            className="inline-flex h-[8.75vw] min-h-[2.75rem] w-[33.58vw] items-center justify-center self-start rounded-full bg-[#333] font-sans text-[3.31vw] font-bold leading-[1.071] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]"
                        >
                            {ctaLabel}
                        </a>
                    ))}
            </div>
        </section>
    );
};
